//? for general info on stencil store, see https://github.com/ionic-team/stencil-store
//? for info on changing complex state, see https://stenciljs.com/docs/reactive-data#handling-arrays-and-objects

import {createStore} from "@stencil/store";
import {getRawAssets, getUserAssets, getRawCategories, getUserCategories, vNotificationAPI, postLegacyUserAssets, postLegacyUserCategories} from "./apiService";
import type {Stage, RawAsset, UserAsset, RawCategory, UserCategory} from "./apiService";
import {addTokenSubscriber, removeTokenSubscriber} from "./authService";

type Category = RawCategory & UserCategory;

export type Categories = {
	isLoading: boolean;
	hasChanges: boolean;
	hasFailedGet: boolean;
	hasFailedPost: boolean;
	data: Category[];
	update: (props: Partial<Omit<Categories, "update">>) => void; // convenience helper to update props without spreading, e.g. state.categories.update({isLoading: false, data: []})
};

type Asset = {
	id: RawAsset["assetId"];
	name: RawAsset["assetName"];
	enabled: UserAsset["enabled"] | null;
};

export type Assets = {
	isLoading: boolean;
	hasChanges: boolean;
	hasFailedGet: boolean;
	hasFailedPost: boolean;
	filter: string;
	data: Asset[];
	update: (props: Partial<Omit<Assets, "update">>) => void; // convenience helper to update props without spreading (e.g. assets.update({data: [], filter: "something"}))
};

const storeId = "userSettingsStore";
let debugMode = false;

/* 
Stencil store lacks 'deep' state-change-tracking against the initial state (after init), making it difficult to detect pseudo-changes (e.g. toggling a checkbox on & off again) and reliably setting the .hasChanged properties.
categoriesDataRef & assetsDataRef in combination with onChange(...) work around this by holding reference copies of the data-properties initially retrieved from fetchCategory and fetchAssets.
They are updated when changes are persisted to the backend via updateCategories and updateAssets.
*/
let categoriesDataRef: Category[];
let assetsDataRef: Asset[];

type InitArgs = {locale: string; stage: Stage; apiVersion?: string; debug?: boolean};

/** Initializes the store with the given args. Retrieves the authentication token, hooks up token subscription, concurrently fetches categories & assets and updates the state. */
const init = async ({locale, stage, apiVersion = vNotificationAPI, debug = debugMode}: InitArgs) => {
	debugMode = debug;

	categoriesDataRef = [];
	assetsDataRef = [];

	state.apiVersion = apiVersion;
	state.locale = locale;
	state.stage = stage;

	try {
		await addTokenSubscriber({subscriberId: storeId, onTokenUpdate: (newToken) => (state.token = newToken)});
		await Promise.all([fetchCategories(), fetchAssets()]);
	} catch (error) {
		console.error("An error occurred while initializing.", error);
		state.hasFailedInitializing = true;
		state.categories.update({isLoading: false, hasFailedGet: true});
		state.assets.update({isLoading: false, hasFailedGet: true});
	}
};

/** Concurrently fetches rawCategories & userCategories, merges both into categories & updates the state. */
const fetchCategories = async () => {
	state.categories.update({isLoading: true, hasChanges: false, hasFailedGet: false});

	try {
		const {stage, token, locale} = state;
		const [rawCategories, userCategories] = await Promise.all([getRawCategories({stage, token, locale}), getUserCategories({stage, token})]);

		const mergedCategories = rawCategories.map((rawCategory) => ({
			...rawCategory,
			channels: userCategories.find((userCategory) => userCategory.name === rawCategory.name)?.channels || [],
		}));

		// always sort system-generated category (if found) to the end
		const sortedCategories = mergedCategories.sort((a, b) => {
			if (a.name === "system-generated") return 1;
			if (b.name === "system-generated") return -1;
			return 0;
		});

		categoriesDataRef = structuredClone(sortedCategories);
		state.categories.update({hasFailedGet: false, data: sortedCategories});
	} catch (error) {
		console.error("An error occurred while fetching categories.", error);
		state.categories.update({hasFailedGet: true});
	} finally {
		state.categories.update({isLoading: false});
	}
};

/** Persists userCategories to backend, updates categories.hasChanged attribute & categoriesDataRef. */
const updateCategories = async () => {
	if (!state.categories.hasChanges) return;

	state.categories.update({hasFailedPost: false});

	//! Currently, the backend does not support updating only userCategories (although assets is optional), so we need to send all userSettings. The assets part is taken from assetsDataRef, so essentially, the initially fetched data is sent back.
	//! replace with commented out code once the backend is ready

	try {
		const {stage, token} = state;
		// const categories = state.categories.data.map(({name, channels}) => ({name, channels})); // reduce Category[] to UserCategory[]
		const legacySettings = {
			categories: state.categories.data.map(({name, channels}) => ({name, channels})), // reduce Category[] to UserCategory[]
			assets: assetsDataRef.filter(({enabled}) => enabled !== null).map(({id, enabled}) => ({id, enabled})), // reduce Asset[] to UserAsset[]
		};

		await postLegacyUserCategories({stage, token, legacySettings});
		// await postLegacyUserCategories({stage, token, categories});

		categoriesDataRef = structuredClone(state.categories.data);
		state.categories.update({hasChanges: false, hasFailedPost: false});
	} catch (error) {
		console.error("An error occurred while posting userCategories.", error);
		state.categories.update({hasFailedPost: true});
	}
};

/** Reverts categories to their unmodified state after the initial fetch. */
const resetCategories = () => {
	state.categories.update({isLoading: false, hasChanges: false, hasFailedGet: false, hasFailedPost: false, data: categoriesDataRef});
};

/** Concurrently fetches rawAssets & userAssets, merges both into assets & updates the state. */
const fetchAssets = async () => {
	state.assets.update({isLoading: true, hasChanges: false, hasFailedGet: false});

	try {
		const {stage, token} = state;
		const [rawAssets, userAssets] = await Promise.all([getRawAssets({stage, token}), getUserAssets({stage, token})]);

		const mergedAssets: Asset[] = rawAssets.map(({assetId: rawId, assetName: rawName}) => ({
			id: rawId,
			name: rawName,
			enabled: userAssets.find(({id: userId}) => userId === rawId)?.enabled ?? null,
		}));

		assetsDataRef = structuredClone(mergedAssets);
		state.assets.update({hasFailedGet: false, data: mergedAssets});
	} catch (error) {
		console.error("An error occurred while fetching assets.", error);
		state.assets.update({hasFailedGet: true});
	} finally {
		state.assets.update({isLoading: false});
	}
};

/** Persists userAssets to backend, updates assets.hasChanged attribute & assetsDataRef. */
const updateAssets = async () => {
	if (!state.assets.hasChanges) return;

	state.assets.update({hasFailedPost: false});

	//! Currently, the backend does not support updating only userAssets, so we need to send all userSettings. The categories part is taken from categoriesDataRef, so essentially, the initially fetched data is sent back.
	//! replace with commented out code once the backend is ready

	try {
		const {stage, token} = state;
		// const assets = state.assets.data.filter(({enabled}) => enabled !== null).map(({id, enabled}) => ({id, enabled})); // reduce Asset[] to UserAsset[]
		const legacySettings = {
			categories: categoriesDataRef.map(({name, channels}) => ({name, channels})), // reduce Category[] to UserCategory[]
			assets: state.assets.data.filter(({enabled}) => enabled !== null).map(({id, enabled}) => ({id, enabled})), // reduce Asset[] to UserAsset[]
		};

		await postLegacyUserAssets({stage, token, legacySettings});
		// await postUserAssets({stage, token, assets});

		assetsDataRef = structuredClone(state.assets.data);
		state.assets.update({hasChanges: false, hasFailedPost: false});
	} catch (error) {
		console.error("An error occurred while posting userAssets.", error);
		state.assets.update({hasFailedPost: true});
	}
};

/** Reverts assets to their unmodified state after the initial fetch. */
const resetAssets = () => {
	state.assets.update({isLoading: false, hasChanges: false, hasFailedGet: false, hasFailedPost: false, data: assetsDataRef});
};

const shouldUpdate = (newValue: unknown, oldValue: unknown, propChanged: string) => {
	const stateChanged = JSON.stringify(newValue) !== JSON.stringify(oldValue);

	if (debugMode) {
		console.group("UserSettings Update");
		console.log("propChanged:  ", propChanged);
		console.log("oldValue:     ", oldValue);
		console.log("newValue:     ", newValue);
		console.log("stateChanged: ", stateChanged);
		console.groupEnd();
	}

	return stateChanged;
};

type UserSettingsStore = {
	token: string | null;
	apiVersion: string | null;
	locale: string | null;
	stage: Stage | null;

	hasFailedInitializing: boolean;

	categories: Categories;
	assets: Assets;

	init: (args: InitArgs) => Promise<void>;
	reset: () => void;

	updateCategories: () => Promise<void>;
	resetCategories: () => void;
	updateAssets: () => Promise<void>;
	resetAssets: () => void;
};

// Extracted into separate functions to prevent circular dependencies in type inference.
const _updateCategoriesState = (props: Partial<Omit<Categories, "update">>) => (state.categories = {...state.categories, ...props});
const _updateAssetsState = (props: Partial<Omit<Assets, "update">>) => (state.assets = {...state.assets, ...props});

// eslint-disable-next-line @typescript-eslint/unbound-method
const {state, onChange, reset} = createStore<UserSettingsStore>(
	{
		token: null,
		apiVersion: null,
		locale: null,
		stage: null,

		hasFailedInitializing: false,

		categories: {
			isLoading: true,
			hasChanges: false,
			hasFailedGet: false,
			hasFailedPost: false,
			data: [],
			update: _updateCategoriesState,
		},

		assets: {
			isLoading: true,
			hasChanges: false,
			hasFailedGet: false,
			hasFailedPost: false,
			filter: "",
			data: [],
			update: _updateAssetsState,
		},

		init,
		reset: () => {
			removeTokenSubscriber(storeId);
			reset();
		},

		updateCategories,
		resetCategories,
		updateAssets,
		resetAssets,
	},
	shouldUpdate,
);

/** Updates categories.hasChanges only if categories.data has (really) changed (ignores the initial fetch) */
onChange("categories", () => state.categories.update({hasChanges: JSON.stringify(state.categories.data) !== JSON.stringify(categoriesDataRef)}));

/** Updates assets.hasChanges only if assets.data has (really) changed (ignores the initial fetch) */
onChange("assets", () => state.assets.update({hasChanges: JSON.stringify(state.assets.data) !== JSON.stringify(assetsDataRef)}));

export {state as userSettingsStore};
